// scenes/categories/CategorieForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { categorieService } from 'services/categorieService';

const CategorieForm = ({ open, onClose, onSuccess, categorie, isEditing }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // États du formulaire
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    image: null,
    imagePreview: null,
  });

  // Réinitialiser le formulaire quand on ouvre/ferme
  useEffect(() => {
    if (open) {
      if (isEditing && categorie) {
        setFormData({
          nom: categorie.nom || '',
          description: categorie.description || '',
          image: null,
          imagePreview: categorie.image || null,
        });
      } else {
        setFormData({
          nom: '',
          description: '',
          image: null,
          imagePreview: null,
        });
      }
      setError('');
    }
  }, [open, isEditing, categorie]);

  // Gestion des changements dans les champs texte
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Gestion du téléchargement d'image
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('L\'image est trop volumineuse (max 5MB)');
        return;
      }

      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner une image valide');
        return;
      }

      setFormData(prev => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
      setError('');
    }
  };

  // Supprimer l'image
  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
  };

  // Validation du formulaire
  const validateForm = () => {
    if (!formData.nom.trim()) {
      setError('Le nom de la catégorie est obligatoire');
      return false;
    }
    if (formData.nom.length > 100) {
      setError('Le nom ne peut pas dépasser 100 caractères');
      return false;
    }
    if (formData.description.length > 500) {
      setError('La description ne peut pas dépasser 500 caractères');
      return false;
    }
    return true;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isEditing) {
        await categorieService.updateCategorie(categorie.id, formData);
      } else {
        await categorieService.createCategorie(formData);
      }
      
      onSuccess();
    } catch (err) {
      console.error('Erreur de soumission:', err);
      
      // Gestion des erreurs spécifiques
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.nom) {
          setError(`Nom: ${errorData.nom.join(', ')}`);
        } else if (errorData.detail) {
          setError(errorData.detail);
        } else if (errorData.non_field_errors) {
          setError(errorData.non_field_errors.join(', '));
        } else {
          setError('Une erreur est survenue lors de l\'enregistrement');
        }
      } else {
        setError(err.message || 'Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            {isEditing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Champ Image */}
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              Image de la catégorie (optionnel)
            </Typography>
            <Box
              sx={{
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              onClick={() => document.getElementById('image-upload').click()}
            >
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              
              {formData.imagePreview ? (
                <Box>
                  <Box
                    component="img"
                    src={formData.imagePreview}
                    alt="Preview"
                    sx={{
                      width: 150,
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 1,
                      mb: 2,
                    }}
                  />
                  <Box display="flex" justifyContent="center" gap={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById('image-upload').click();
                      }}
                    >
                      Changer
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage();
                      }}
                    >
                      Supprimer
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body2" color="textSecondary">
                    Cliquez pour télécharger une image
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    PNG, JPG, JPEG (max 5MB)
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Champ Nom */}
          <TextField
            fullWidth
            label="Nom de la catégorie *"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
            required
            disabled={loading}
            InputProps={{
              startAdornment: (
                <ImageIcon sx={{ mr: 1, color: 'action.active' }} />
              ),
            }}
            helperText="Nom unique pour identifier la catégorie"
          />

          {/* Champ Description */}
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
            disabled={loading}
            helperText="Description détaillée de la catégorie (max 500 caractères)"
            inputProps={{ maxLength: 500 }}
          />
          
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              * Champs obligatoires
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {loading ? 'Enregistrement...' : isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CategorieForm;